import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, ArrowRight, Shield } from 'lucide-react';
import { getComparison } from '../data/comparisons';

const SITE = 'https://githubmate.ai';

const ComparisonPage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  // URLs are /compare/githubmate-vs-<competitor>; the data key is just <competitor>.
  const competitorSlug = slug.replace(/^githubmate-vs-/, '');
  const data = getComparison(competitorSlug);

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Comparison not found</h1>
        <p className="text-gray-600 mb-6">
          We don't have a comparison page for that tool yet.
        </p>
        <Link to="/compare" className="btn-primary inline-flex items-center">
          View all comparisons <ArrowRight className="h-4 w-4 ml-2" />
        </Link>
      </div>
    );
  }

  const url = `${SITE}/compare/githubmate-vs-${data.slug}`;
  const heading = `GitHubMate vs ${data.competitor}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: data.title,
        description: data.metaDescription,
        isPartOf: { '@id': `${SITE}/#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'en-US',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Comparisons', item: `${SITE}/compare` },
          { '@type': 'ListItem', position: 3, name: heading, item: url },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        isPartOf: { '@id': `${url}#webpage` },
        mainEntity: data.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={`${heading} — Free AI Code Security Scanner Comparison`} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-charcoal-bg">
        <div className="max-w-5xl mx-auto px-4 py-12">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/compare" className="hover:text-blue-600">Comparisons</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700">{heading}</span>
          </nav>

          {/* Hero */}
          <header className="mb-10">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">{heading}</h1>
            <p className="text-xl text-gray-600 mb-6">{data.tagline}</p>
            <Link to="/" className="btn-primary inline-flex items-center">
              <Shield className="h-4 w-4 mr-2" /> Run a free scan
            </Link>
          </header>

          {/* Intro */}
          <section className="prose max-w-none mb-10">
            <p className="text-gray-700 leading-relaxed">{data.intro}</p>
          </section>

          {/* Comparison table */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              GitHubMate vs {data.competitor}: feature comparison
            </h2>
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="w-full text-left bg-charcoal-card">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 font-semibold text-gray-700">Feature</th>
                    <th className="px-4 py-3 font-semibold text-blue-700">GitHubMate</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">{data.competitor}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <tr key={i} className={i % 2 ? 'bg-gray-50/50' : ''}>
                      <td className="px-4 py-3 font-medium text-gray-900">{row.feature}</td>
                      <td className="px-4 py-3 text-gray-700">{row.githubmate}</td>
                      <td className="px-4 py-3 text-gray-700">{row.competitor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* When to use which */}
          <section className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 mb-4">When to choose GitHubMate</h2>
              <ul className="space-y-2">
                {data.whenGithubmate.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card">
              <h2 className="text-xl font-bold text-gray-900 mb-4">When to choose {data.competitor}</h2>
              <ul className="space-y-2">
                {data.whenCompetitor.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <ArrowRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* About competitor */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">What is {data.competitor}?</h2>
            <p className="text-gray-700 leading-relaxed">{data.competitorSummary}</p>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently asked questions</h2>
            <div className="space-y-4">
              {data.faqs.map((f, i) => (
                <div key={i} className="card">
                  <h3 className="font-semibold text-gray-900 mb-2">{f.q}</h3>
                  <p className="text-gray-700">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Verdict + CTA */}
          <section className="rounded-3xl border border-charcoal-border bg-gradient-to-br from-lime/15 via-charcoal-card to-accent-teal/10 p-8 text-center text-gray-900 mb-8">
            <h2 className="text-2xl font-bold mb-3">The verdict</h2>
            <p className="text-muted max-w-3xl mx-auto mb-6">{data.verdict}</p>
            <Link
              to="/"
              className="btn-primary inline-flex items-center py-3 px-6"
            >
              <Shield className="h-5 w-5 mr-2" /> Scan a repository free
            </Link>
          </section>

          {/* Related comparisons */}
          <section className="text-center">
            <Link to="/compare" className="text-blue-600 hover:underline font-medium">
              See all GitHubMate comparisons →
            </Link>
          </section>
        </div>
      </div>
    </>
  );
};

export default ComparisonPage;
